import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react'
import { useRouteError } from 'react-router-dom'
import { AppErrorScreen } from '@/app/AppErrorScreen'
import { reportBootstrapError } from '@/lib/bootstrapError'

interface AppErrorBoundaryState {
  error: Error | null
}

/** Top-level render-error boundary (outermost in main.tsx, above ThemeProvider -
 *  global.css defines the CSS vars on :root, so the fallback styles resolve even
 *  when the theme provider itself is what threw). Data-router routes swallow
 *  in-route throws before they reach this class, so RouteErrorScreen (below) is
 *  registered as errorElement on the top-level routes as well. */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  AppErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[AppErrorBoundary] Uncaught render error:', error, info)
    reportBootstrapError('render')
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <AppErrorScreen
          stage="render"
          error={this.state.error}
          action={{ label: 'Reload', onClick: () => window.location.reload() }}
        />
      )
    }
    return this.props.children
  }
}

/** errorElement for createBrowserRouter routes - the data router catches render
 *  throws inside routes with its own default page, so the class boundary never
 *  sees them. */
export function RouteErrorScreen() {
  const error = useRouteError()
  useEffect(() => {
    console.error('[RouteErrorScreen] route render error:', error)
    reportBootstrapError('route-render')
  }, [error])
  return (
    <AppErrorScreen
      stage="route-render"
      error={error}
      action={{ label: 'Reload', onClick: () => window.location.reload() }}
    />
  )
}
